import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, MessageSquare, CalendarCheck, Sparkles, RefreshCcw, Bot } from 'lucide-react';
const agents = [{
  id: 'reservations',
  icon: Phone,
  name: 'Reservation Agent',
  desc: 'Handles bookings 24/7',
  chat: [{
    sender: 'guest',
    text: 'Hi, do you have a table for 4 tonight around 7?'
  }, {
    sender: 'ai',
    text: 'I can seat you at 7:15 PM in the main dining room. Would you like me to lock that in?'
  }]
}, {
  id: 'concierge',
  icon: MessageSquare,
  name: 'Guest Concierge',
  desc: 'Answers questions, drives upgrades',
  chat: [{
    sender: 'guest',
    text: 'Do you have vegan options?'
  }, {
    sender: 'ai',
    text: 'Yes! We have a dedicated plant-based menu featuring our signature roasted eggplant. I can add a note to your booking.'
  }]
}, {
  id: 'events',
  icon: CalendarCheck,
  name: 'Event Coordinator',
  desc: 'Manages function enquiries',
  chat: [{
    sender: 'guest',
    text: 'Looking for a space for a 30th birthday, about 40 people.'
  }, {
    sender: 'ai',
    text: "Perfect. Our private terrace holds up to 50. I've just emailed you our function packages and minimum spend details."
  }]
}, {
  id: 'vip',
  icon: Sparkles,
  name: 'VIP Recognition',
  desc: 'Flags repeat guests',
  chat: [{
    sender: 'system',
    text: 'Incoming call: Sarah Jenkins (VIP)'
  }, {
    sender: 'ai',
    text: 'Hi Sarah, welcome back! Would you like your usual corner booth for this Friday?'
  }]
}, {
  id: 'followup',
  icon: RefreshCcw,
  name: 'Follow-Up Agent',
  desc: 'Review requests, win-back',
  chat: [{
    sender: 'ai',
    text: 'Hi Mark, thanks for dining with us last night. How was the steak?'
  }, {
    sender: 'guest',
    text: "It was incredible, best I've had in years."
  }, {
    sender: 'ai',
    text: 'Love to hear it! If you have a moment, a Google review helps us out immensely. [Link]'
  }]
}, {
  id: 'ops',
  icon: Bot,
  name: 'Ops Copilot',
  desc: 'Staff assistance',
  chat: [{
    sender: 'staff',
    text: "What's the recipe for the seasonal spritz?"
  }, {
    sender: 'ai',
    text: 'Seasonal Spritz: 45ml Aperol, 15ml Yuzu, 90ml Prosecco, splash of soda, grapefruit garnish.'
  }]
}];
export function AgentShowcase() {
  const [activeAgent, setActiveAgent] = useState(agents[0]);
  return <section className="py-32 bg-brand-black relative z-10" data-id="element-331">
      <div className="max-w-7xl mx-auto px-6" data-id="element-332">
        <div className="text-center mb-20" data-id="element-333">
          <h2 className="text-4xl md:text-6xl font-display mb-6 leading-[1.0] tracking-tight text-balance text-white" data-id="element-334">
            Meet your AI <br className="hidden md:block" data-id="element-335" />
            <span className="text-gray-500" data-id="element-336">hospitality team.</span>
          </h2>
          <p className="text-xl text-gray-400 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-337">
            Always-on agents that handle what your team shouldn't have to.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-start max-w-5xl mx-auto" data-id="element-338">
          {/* Left: Agent Selector */}
          <div className="lg:col-span-5 flex flex-col gap-2" data-id="element-339">
            {agents.map(agent => {
            const isActive = activeAgent.id === agent.id;
            return <button key={agent.id} onClick={() => setActiveAgent(agent)} className={`flex items-center gap-4 p-4 rounded-xl text-left transition-all duration-300 border-l-2 ${isActive ? 'bg-brand-orange/10 border-brand-orange' : 'bg-transparent border-transparent hover:bg-white/5'}`} data-id="element-340">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isActive ? 'bg-brand-orange/20 text-brand-orange' : 'bg-white/5 text-gray-400'}`} data-id="element-341">
                    <agent.icon className="w-5 h-5" data-id="element-342" />
                  </div>
                  <div data-id="element-343">
                    <h4 className={`font-medium ${isActive ? 'text-white' : 'text-gray-300'}`} data-id="element-344">
                      {agent.name}
                    </h4>
                    <p className="text-xs text-gray-500" data-id="element-345">{agent.desc}</p>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Chat Preview */}
          <div className="lg:col-span-7 bg-[#0A0A0A] border border-white/10 rounded-3xl p-6 md:p-10 shadow-2xl h-[400px] flex flex-col" data-id="element-346">
            <div className="flex items-center gap-3 mb-8 border-b border-white/5 pb-4" data-id="element-347">
              <div className="w-8 h-8 rounded-full bg-brand-orange/20 flex items-center justify-center" data-id="element-348">
                <activeAgent.icon className="w-4 h-4 text-brand-orange" data-id="element-349" />
              </div>
              <div data-id="element-350">
                <h4 className="text-sm font-medium text-white" data-id="element-351">
                  {activeAgent.name}
                </h4>
                <span className="text-[10px] text-green-400 uppercase tracking-widest flex items-center gap-1" data-id="element-352">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" data-id="element-353" />
                  Online
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto flex flex-col gap-6" data-id="element-354">
              <AnimatePresence mode="wait" data-id="element-355">
                <motion.div key={activeAgent.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.3
              }} className="flex flex-col gap-6" data-id="element-356">
                  {activeAgent.chat.map((msg, i) => <div key={i} className={`flex ${msg.sender === 'ai' ? 'justify-start' : msg.sender === 'system' ? 'justify-center' : 'justify-end'}`} data-id="element-357">
                      {msg.sender === 'system' ? <div className="bg-white/5 px-4 py-1.5 rounded-full text-xs text-gray-400" data-id="element-358">
                          {msg.text}
                        </div> : <div className={`max-w-[80%] p-4 rounded-2xl text-sm leading-relaxed ${msg.sender === 'ai' ? 'bg-[#111] border border-white/10 text-gray-200 rounded-tl-sm' : 'bg-brand-orange text-white rounded-tr-sm'}`} data-id="element-359">
                          {msg.text}
                        </div>}
                    </div>)}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-20 border-t border-white/5 pt-12" data-id="element-360">
          {['24/7 coverage', '8 languages', '<2s response time', 'Zero hold time'].map((stat, i) => <div key={i} className="text-center" data-id="element-361">
              <p className="text-sm font-medium text-gray-400 uppercase tracking-widest" data-id="element-362">
                {stat}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}